import { useMemo, useState } from 'react';

// Client-side column sorting for tables whose rows are already fully loaded in the
// browser (e.g. the per-entity ledger tabs on the Item/Supplier/Customer detail
// pages — not server-paginated). Pairs with the shared <SortableTh> header, using
// the same { sort, dir, toggleSort } shape as the server-side usePaginatedList so
// the two feel identical.

export type SortDir = 'asc' | 'desc';
export type SortValue = string | number | null | undefined;

function isEmpty(v: SortValue): boolean {
  return v === null || v === undefined || v === '';
}

/**
 * Compare two sort values. Numbers compare numerically; everything else via
 * locale-aware, numeric-aware string compare. Empty/null values always sort LAST,
 * independent of direction (handled by the caller applying this before the dir
 * factor). Returns a signed number for the ascending order of real values.
 */
function compareValues(a: SortValue, b: SortValue): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

/**
 * Pure, stable sort of `rows` by `key`/`dir` using the provided accessors.
 * - Unknown key → rows returned unchanged.
 * - Empty/null values sort last in BOTH directions.
 * - Ties keep original order (stable).
 */
export function sortRows<T>(
  rows: T[],
  key: string,
  dir: SortDir,
  accessors: Record<string, (row: T) => SortValue>,
): T[] {
  const accessor = accessors[key];
  if (!accessor) return rows;
  const factor = dir === 'asc' ? 1 : -1;
  return rows
    .map((row, i) => ({ row, i }))
    .sort((a, b) => {
      const av = accessor(a.row);
      const bv = accessor(b.row);
      const aEmpty = isEmpty(av);
      const bEmpty = isEmpty(bv);
      if (aEmpty || bEmpty) {
        if (aEmpty && bEmpty) return a.i - b.i; // stable
        return aEmpty ? 1 : -1; // empties last, regardless of dir
      }
      const cmp = compareValues(av, bv);
      return cmp !== 0 ? cmp * factor : a.i - b.i; // stable tiebreak
    })
    .map((d) => d.row);
}

export interface UseClientSortResult<T> {
  sorted: T[];
  sort: string;
  dir: SortDir;
  toggleSort: (key: string) => void;
}

/**
 * Hook wrapper around sortRows: owns { sort, dir } state and re-sorts `rows`.
 * `toggleSort(key)` flips direction on the active key, or selects a new key
 * (starting 'asc') — same semantics as usePaginatedList.
 *
 * NOTE: pass a STABLE `accessors` object (module constant or useMemo) — a fresh
 * object each render re-runs the sort each render (harmless for small tables, but
 * avoidable).
 */
export function useClientSort<T>(
  rows: T[],
  opts: {
    defaultKey: string;
    defaultDir?: SortDir;
    accessors: Record<string, (row: T) => SortValue>;
  },
): UseClientSortResult<T> {
  const [sort, setSort] = useState(opts.defaultKey);
  const [dir, setDir] = useState<SortDir>(opts.defaultDir ?? 'asc');

  function toggleSort(key: string) {
    if (key === sort) {
      setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(key);
      setDir('asc');
    }
  }

  const sorted = useMemo(
    () => sortRows(rows, sort, dir, opts.accessors),
    [rows, sort, dir, opts.accessors],
  );

  return { sorted, sort, dir, toggleSort };
}
